import {Component, forwardRef, Input, QueryList, ViewChildren} from '@angular/core';
import {SubscribedComponent} from "../../subscribed.component";
import {ColDef, ColType} from "../../interfaces/colDef";
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {NbCheckboxComponent} from "@nebular/theme";
import _ from "lodash";
import {Globals} from "../../shared/Globals";
import {SYNTHETIC_FILTER_KEYS} from "../../dual-axis/dual-axis.component";

export interface TableOptions{
  display: {col: string, shown: boolean}[],
  filter: any,
  sorts: {col: string, dir: number, meta?: any}[],
  others?: {[col: string]: any}
}

export interface TableOptionsDelegate{
  filter: (col: ColDef, $event: MouseEvent) => void,
  clearFilter: (colName: string) => void,
  setSort: (col: ColDef) => void,   // select (type-aware default dir) or flip the primary sort
}

@Component({
    selector: 'bst-table-options',
    templateUrl: './table-options.component.html',
    styleUrls: ['./table-options.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TableOptionsComponent),
            multi: true
        }
    ],
    standalone: false
})
export class TableOptionsComponent extends SubscribedComponent implements ControlValueAccessor{

  @ViewChildren('row') public rows: QueryList<NbCheckboxComponent>;

  @Input() delegate: TableOptionsDelegate;
  @Input() colDefs: ColDef[] = [];
  @Input() hideColumns = false; // card-mode lists: column show/hide is meaningless
  @Input() hideSort = true; // sort only has a home in card view (table view sorts via headers)
  // Columns offered in the Sort section. Separate from colDefs because sort must include
  // sticky columns (e.g. the Projects/Clients name lives in stickyColumns), whereas the
  // Columns show/hide section operates on the standard colDefs only. Falls back to colDefs.
  @Input() sortColDefs: ColDef[] = [];
  // Columns the FILTER section may list/offer. Like sortColDefs, this includes sticky
  // columns (e.g. the market list's New / Score) so a filter on a sticky column is visible
  // and removable here, not just silently applied. The Columns show/hide section stays on
  // colDefs (standard only) so sticky columns don't become hideable. Falls back to colDefs.
  @Input() filterColDefs: ColDef[] = [];
  // @Input() otherDefs: ColDef[] = [];
  @Input() options: TableOptions = {
    display: this.colDefs.map(o => {
      return {col: o.name, shown:true};
    }),
    sorts: [],
    filter: {},
    others: {}
  };
  @Input() onExport = null;
  @Input() initialSection: 'filters' | 'columns' = 'filters';
  public columnsOpen = false;     // disclosure state for the Columns section
  public addingFilter = false;    // shows the column picker for + Add filter
  public columnSearch = '';       // "Find column…" filter for the columns list
  public dragging = null;
  public disabled: boolean = false;
  public columns: {control: FormControl, col: ColDef}[];
  // public others:{control: FormControl, col: ColDef}[] = [];
  ColType = ColType;
  Globals = Globals;

  // Function to call when the value changes.
  onChange = (value: TableOptions) => {};
  // Function to call when the input is touched (when a star is clicked).
  onTouched = () => {};

  constructor() {
    super();
  }

  writeValue(options: TableOptions): void {
    this.columns = new Array(this.colDefs.length);
    let maxindex = 0;
    for(let col of this.colDefs){
      let myindex = options ? _.findIndex(options.display, o => o.col === col.name) : -1;
      let display: boolean = true;
      if(myindex === -1){
        myindex = maxindex+1;
      }
      else{
        display = options ? options.display[myindex].shown : true;
      }
      maxindex = Math.max(myindex, maxindex);
      // const curfilter = options.filter[col.name];
      // col.filter = typeof curfilter !== 'undefined' && curfilter !== null ? curfilter : null;
      const control = new FormControl({value: col.disabled && col.required ? true : display,
        disabled: col.disabled || this.disabled});
      this.columns[myindex] = {
        control: control,
        col: col
      };
    }

    // if(this.otherDefs){
    //   this.others = this.otherDefs.map(col => {
    //     const myval = this.options && this.options.others ? (typeof this.options.others[col.name] !== 'undefined' ? this.options.others[col.name] : null) : null;
    //     const validators = col.required ? [BSValidators.required] : [];
    //     const control = new FormControl({value: myval, disabled: col.disabled || this.disabled}, validators);
    //     this.subs.push(control.valueChanges.subscribe(newval => {
    //       this.format();
    //     }));
    //     return { control, col };
    //   });
    // }

    this.format();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.options) { this.writeValue(this.options || null); }
    this.columnsOpen = this.initialSection === 'columns';
  }

  /** Filterable columns (col.filter !== false). Uses filterColDefs (incl. sticky columns)
   *  when provided so sticky-column filters are visible; falls back to colDefs. */
  public get filterableColumns(): ColDef[] {
    const source = this.filterColDefs?.length ? this.filterColDefs : this.colDefs;
    return (source || []).filter(c => c.filter !== false);
  }

  /** Active column filters as {col,title}, excluding synthetic keys. */
  public get activeFilters(): { col: ColDef; key: string }[] {
    const f = this.options?.filter || {};
    return this.filterableColumns
      .filter(c => SYNTHETIC_FILTER_KEYS.indexOf(c.name) === -1
                && f[c.name] !== null && f[c.name] !== undefined)
      .map(c => ({ col: c, key: c.name }));
  }

  /** Columns offered in + Add filter: filterable and not already filtered. */
  public get addableColumns(): ColDef[] {
    const filterable = this.filterableColumns;
    const active = new Set(
      filterable
        .filter(c => SYNTHETIC_FILTER_KEYS.indexOf(c.name) === -1
                  && (this.options?.filter?.[c.name] ?? null) !== null)
        .map(c => c.name)
    );
    return filterable
      .filter(c => !active.has(c.name))
      .filter(c => !this.columnSearch
                || (c.title || c.name).toLowerCase().includes(this.columnSearch.toLowerCase()));
  }

  public get shownCount(): number {
    return (this.columns || []).filter(c => c && c.control.value).length;
  }

  /** The active primary sort, fed to the shared <bst-sort-options> (highlight + glyph). */
  public get currentSort(): { col: string, dir: number } | null {
    return this.options?.sorts?.[0] || null;
  }

  public chooseSort(col: ColDef): void {
    this.delegate?.setSort(col);
  }

  public pickFilter(col: ColDef, $event: MouseEvent): void {
    this.addingFilter = false;
    this.columnSearch = '';
    this.delegate?.filter(col, $event);   // opens FilterDialogComponent; dual-axis applies + resets
  }

  public editFilter(key: string, $event: MouseEvent): void {
    const col = this.filterableColumns.find(c => c.name === key);
    if (col) { this.delegate?.filter(col, $event); }
  }

  public removeFilter(key: string): void {
    this.delegate?.clearFilter(key);
  }

  public get formatted(){
    const options:TableOptions ={
      display: (this.columns || []).map(o => {
        return {
          col: o.col.name,
          shown: <boolean>(o.control.value)
        };
      }),
      filter: this.options ? this.options.filter : null,
      sorts: this.options?.sorts || [],
      others: this.options?.others
    };
    return options;
  }

  public format(){
    if(this.columns) {
      this.onChange(this.formatted);
    }
  }

  resort(old_index: number, new_index: number) {
    if (new_index >= this.columns.length) {
      let k = new_index - this.columns.length + 1;
      while (k--) {
        this.columns.push(undefined);
      }
    }
    this.columns.splice(new_index, 0, this.columns.splice(old_index, 1)[0]);
    this.dragging = null;
    this.format();
  }
}
